import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as faceapi from 'face-api.js';
import { Camera, Loader2, RotateCcw, X, LogIn, LogOut, MapPin, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const FACE_MODELS_URL = `${process.env.PUBLIC_URL || ''}/face-models`;
const DEFAULT_THRESHOLD = 0.68;

// One-tap portal clock in/out with a required selfie + face match.
// Auto-picks the nearest station and enforces the geofence server-side.
export const QuickPunchModal = ({ open, type, status, onClose, onDone }) => {
  const [modelsLoading, setModelsLoading] = useState(false);
  const [streamActive, setStreamActive] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [matchInfo, setMatchInfo] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [coords, setCoords] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const coordsRef = useRef(null);
  const refDescRef = useRef(null);
  const refPromiseRef = useRef(null);

  const threshold = status?.face_match_threshold || DEFAULT_THRESHOLD;
  const hasRef = !!status?.has_reference_photo;
  const getToken = () => localStorage.getItem('portal_token') || localStorage.getItem('token');

  const modelsReady = () =>
    faceapi.nets.tinyFaceDetector.isLoaded &&
    faceapi.nets.faceLandmark68Net.isLoaded &&
    faceapi.nets.faceRecognitionNet.isLoaded;

  const ensureModels = async () => {
    if (modelsReady()) return true;
    setModelsLoading(true);
    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(FACE_MODELS_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(FACE_MODELS_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(FACE_MODELS_URL),
      ]);
      return modelsReady();
    } catch {
      setErr('Could not load face models. Check your connection and retry.');
      return false;
    } finally {
      setModelsLoading(false);
    }
  };

  const detectFace = async (input) => {
    for (const opts of [{ inputSize: 320, scoreThreshold: 0.3 }, { inputSize: 512, scoreThreshold: 0.2 }, { inputSize: 608, scoreThreshold: 0.15 }]) {
      const det = await faceapi.detectSingleFace(input, new faceapi.TinyFaceDetectorOptions(opts)).withFaceLandmarks().withFaceDescriptor();
      if (det) return det;
    }
    return null;
  };

  const computeRef = async () => {
    if (refDescRef.current) return refDescRef.current;
    if (refPromiseRef.current) return refPromiseRef.current;
    const url = status?.photo_url;
    if (!url) return null;
    refPromiseRef.current = (async () => {
      try {
        if (!(await ensureModels())) return null;
        const refUrl = /^https?:\/\//i.test(url) ? url : `${API}${url}`;
        const img = await faceapi.fetchImage(refUrl);
        const det = await detectFace(img);
        if (det) { refDescRef.current = det.descriptor; return det.descriptor; }
      } catch { /* punch still works, flagged for HR */ }
      finally { refPromiseRef.current = null; }
      return null;
    })();
    return refPromiseRef.current;
  };

  const startCamera = async () => {
    try {
      if (!streamRef.current || !streamRef.current.active) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } },
          audio: false,
        });
      }
      if (videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
        await videoRef.current.play();
        setStreamActive(true);
      }
    } catch {
      setErr('Camera access denied. Allow camera permission and retry.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreamActive(false);
  };

  const warmGps = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => { const c = { lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy }; coordsRef.current = c; setCoords(c); },
      () => {},
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    );
  };

  const getFreshPosition = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    const ok = (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy });
    navigator.geolocation.getCurrentPosition(
      ok,
      () => navigator.geolocation.getCurrentPosition(ok, () => resolve(null), { enableHighAccuracy: false, timeout: 8000, maximumAge: 30000 }),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 8000 },
    );
  });

  // Lifecycle: open → boot camera + models + GPS + ref descriptor.
  useEffect(() => {
    if (!open) return undefined;
    setSelfie(null); setMatchInfo(null); setErr(null); setSubmitting(false);
    ensureModels();
    warmGps();
    if (hasRef) computeRef();
    const t = setTimeout(() => startCamera(), 100);
    return () => { clearTimeout(t); stopCamera(); refDescRef.current = null; refPromiseRef.current = null; coordsRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Re-attach the stream when returning to the live view after a retake.
  useEffect(() => {
    if (open && !selfie && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
      setStreamActive(true);
    }
  }, [open, selfie]);

  const capture = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    setAnalyzing(true); setErr(null);
    try {
      const v = videoRef.current, c = canvasRef.current;
      const w = v.videoWidth || 480, h = v.videoHeight || 480;
      c.width = w; c.height = h;
      const ctx = c.getContext('2d');
      ctx.save(); ctx.translate(w, 0); ctx.scale(-1, 1); ctx.drawImage(v, 0, 0, w, h); ctx.restore();
      const dataUrl = c.toDataURL('image/jpeg', 0.85);
      setSelfie(dataUrl);
      const ready = await ensureModels();
      let info = { distance: null, score: null, matched: null };
      if (ready) {
        const det = await detectFace(c);
        if (det) {
          let ref = refDescRef.current;
          if (!ref && hasRef) ref = await computeRef();
          if (ref) {
            const distance = faceapi.euclideanDistance(ref, det.descriptor);
            info = { distance, score: Math.max(0, Math.min(1, 1 - distance / (2 * threshold))), matched: distance < threshold };
          }
        }
      }
      setMatchInfo(info);
    } catch {
      setMatchInfo({ distance: null, score: null, matched: null });
    } finally {
      setAnalyzing(false);
    }
  };

  const retake = () => {
    setSelfie(null); setMatchInfo(null);
    if (!streamRef.current || !streamRef.current.active) startCamera();
  };

  const submit = async (forceRepeat = false) => {
    if (!selfie) return;
    setSubmitting(true); setErr(null);
    let pos = coordsRef.current;
    if (!pos) { pos = await getFreshPosition(); if (pos) { coordsRef.current = pos; setCoords(pos); } }
    try {
      const res = await axios.post(`${API}/api/clock/portal/quick-punch`, {
        requested_type: type,
        force_repeat: forceRepeat,
        device_info: navigator.userAgent.slice(0, 180),
        latitude: pos?.lat ?? null,
        longitude: pos?.lng ?? null,
        accuracy: pos?.acc ?? null,
        selfie_b64: selfie,
        face_distance: matchInfo?.distance ?? null,
        face_match_score: matchInfo?.score ?? null,
        has_reference_photo: hasRef,
      }, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (res.data.requires_confirmation) {
        setSubmitting(false);
        if (window.confirm(res.data.message)) return submit(true);
        return;
      }
      stopCamera();
      toast({
        title: res.data.type === 'in' ? 'Clocked in ✓' : 'Clocked out ✓',
        description: res.data.branch_name
          ? `${res.data.branch_name}${res.data.distance_m != null ? ` · ${Math.round(res.data.distance_m)}m away` : ''}`
          : undefined,
      });
      onDone && onDone(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Could not record punch.');
      setSubmitting(false);
    }
  };

  if (!open) return null;
  const isIn = type === 'in';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4" data-testid="quick-punch-modal">
      <div className="bg-white w-full sm:max-w-sm rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className={`px-5 py-3.5 flex items-center justify-between ${isIn ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : 'bg-gradient-to-r from-rose-500 to-orange-500'} text-white`}>
          <div className="flex items-center gap-2">
            {isIn ? <LogIn className="h-5 w-5" /> : <LogOut className="h-5 w-5" />}
            <p className="font-bold text-sm">{isIn ? 'Clock In' : 'Clock Out'}</p>
          </div>
          <button onClick={onClose} disabled={submitting} data-testid="quick-punch-close" className="p-1 rounded-full hover:bg-white/20 disabled:opacity-50">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div className="text-center">
            <p className="text-xs text-gray-500">Verifying</p>
            <p className="font-bold text-gray-900 text-sm">{status?.employee_name}</p>
            {!hasRef && (
              <p className="text-[11px] text-amber-600 mt-0.5 flex items-center justify-center gap-1">
                <AlertTriangle className="h-3 w-3" /> No profile photo — selfie saved for HR review
              </p>
            )}
            {modelsLoading && (
              <p className="text-[11px] text-gray-500 mt-0.5 flex items-center justify-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading face models…
              </p>
            )}
          </div>

          <div className="relative bg-black rounded-2xl overflow-hidden aspect-square">
            {!selfie ? (
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} data-testid="quick-punch-video" />
            ) : (
              <img src={selfie} alt="Selfie" className="w-full h-full object-cover" data-testid="quick-punch-selfie" />
            )}
            <canvas ref={canvasRef} className="hidden" />
            {!selfie && streamActive && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-44 h-56 border-2 border-white/60 rounded-[50%] shadow-[0_0_0_9999px_rgba(0,0,0,0.25)]" />
              </div>
            )}
          </div>

          {err && <div className="bg-rose-50 text-rose-700 text-xs px-3 py-2 rounded-lg" data-testid="quick-punch-error">{err}</div>}

          {matchInfo && matchInfo.matched === true && (
            <div className="text-center py-2 rounded-lg text-sm bg-emerald-50 text-emerald-700 flex items-center justify-center gap-1.5" data-testid="quick-punch-match">
              <CheckCircle2 className="h-4 w-4" /> Face matched · {Math.round((matchInfo.score || 0) * 100)}%
            </div>
          )}
          {matchInfo && matchInfo.matched === false && (
            <div className="text-center py-2 rounded-lg text-sm bg-amber-50 text-amber-700" data-testid="quick-punch-mismatch">
              Face will be reviewed by HR — you can still continue
            </div>
          )}
          {matchInfo && matchInfo.matched === null && (
            <div className="text-center py-2 rounded-lg text-xs bg-gray-50 text-gray-500" data-testid="quick-punch-unverified">
              Selfie saved — HR will verify it.
            </div>
          )}

          {coords && (
            <p className="text-[11px] text-gray-400 flex items-center justify-center gap-1">
              <MapPin className="h-3 w-3" /> Location ready{coords.acc ? ` · ±${Math.round(coords.acc)}m` : ''}
            </p>
          )}

          {!selfie ? (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={onClose} className="py-3 rounded-xl bg-gray-100 text-gray-700 font-medium" data-testid="quick-punch-cancel">Cancel</button>
              <button
                onClick={capture}
                disabled={!streamActive || analyzing}
                className={`py-3 rounded-xl text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2 ${isIn ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
                data-testid="quick-punch-capture"
              >
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Capture
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                onClick={() => submit(false)}
                disabled={submitting}
                className={`w-full py-3.5 rounded-xl text-white font-bold flex items-center justify-center gap-2 shadow-md disabled:opacity-60 ${isIn ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}`}
                data-testid="quick-punch-confirm"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isIn ? <LogIn className="h-4 w-4" /> : <LogOut className="h-4 w-4" />}
                {submitting ? 'Recording…' : `Confirm ${isIn ? 'Clock In' : 'Clock Out'}`}
              </button>
              <button onClick={retake} disabled={submitting} className="w-full py-3 rounded-xl bg-gray-100 text-gray-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50" data-testid="quick-punch-retake">
                <RotateCcw className="h-4 w-4" /> Retake photo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickPunchModal;
